import { useCallback, useMemo, useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { currencyFormatter } from "@common/helpers/currency";
import { pluralizeWithCount } from "@common/helpers/string";
import { Task } from "@common/types/task";
import {
    MilestoneReviewColumn,
    MilestoneReviewView,
} from "@common/types/views/milestoneReviewView";
import { ViewType } from "@common/types/views/views";
import { recognizeScheduleMilestone } from "@api/scheduleMilestone.api";
import { invalidateAfterLifecycleAction } from "@api/scheduleTemplate.api";
import { invalidateAllTaskQueries } from "@api/taskQueryKeys";
import { useScheduleLockedPeriodsDialog } from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";
import { buildScheduleHubUrl } from "@components/accounting/schedulesReport/schedulesReportDrillDown.lib";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { TablePageSubHeader } from "@design-system/TablePageSubHeader/TablePageSubHeader";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { queryClient } from "queryClient";
import { useScope } from "@state/scopeContext.lib";
import {
    confirmMilestoneRows,
    formatMilestoneConfirmToast,
} from "./milestoneReviewConfirm";
import { MilestoneReviewTaskActionBar } from "./MilestoneReviewTaskActionBar";
import {
    MilestoneReviewRow,
    getMilestoneReviewApplications,
    resolveSharedSourceLabel,
    selectDueRows,
    summarizeDueRows,
} from "./milestoneReviewHelpers";
import {
    MilestoneReviewTableCallbacks,
    milestoneReviewViewConfig,
} from "./milestoneReviewViewConfig";
import { SCHEDULE_REVIEW_TOAST_KEY } from "./scheduleReview.constants";
import styles from "./ScheduleReviewTaskBody.module.scss";
import { getSelectedRowIds } from "./scheduleReviewSelection";

interface MilestoneReviewTaskBodyProps {
    selectedTask: Task;
}

const milestoneView: MilestoneReviewView = {
    type: ViewType.MILESTONE_REVIEW,
    options: { bulkActions: true, selection: true, alwaysShowSelection: true },
    columns: [
        { id: MilestoneReviewColumn.SCHEDULE },
        { id: MilestoneReviewColumn.SOURCE },
        { id: MilestoneReviewColumn.MILESTONE },
        { id: MilestoneReviewColumn.RECOGNIZED },
        { id: MilestoneReviewColumn.EXPECTED_DATE },
        { id: MilestoneReviewColumn.POSTING_DATE },
    ],
    filters: {},
};

function formatDueSummary(rows: readonly MilestoneReviewRow[]): string {
    const summary = summarizeDueRows(rows);
    const milestones = pluralizeWithCount("milestone", summary.milestoneCount);
    const across =
        summary.scheduleCount === 1
            ? `on ${rows[0].sourceLabel}`
            : `across ${pluralizeWithCount("schedule", summary.scheduleCount)}`;
    return `${milestones} due ${across} · ${currencyFormatter.format(summary.amount)}`;
}

/**
 * Body of a `milestone_revenue_recognition_review` task: every milestone of
 * the entity whose expected date has arrived, across its schedules, confirmed
 * one by one or all at once. The rows come from the task metadata the daily
 * sync writes; a confirmed row leaves the table until the next sync.
 */
export const MilestoneReviewTaskBody = ({
    selectedTask,
}: MilestoneReviewTaskBodyProps) => {
    const entityId = selectedTask.entityIds?.[0] ?? null;
    const applications = useMemo(
        () => getMilestoneReviewApplications(selectedTask),
        [selectedTask],
    );
    const navigate = useStateAwareNavigate();
    const { toast, error } = useToaster();
    const showLockedPeriods = useScheduleLockedPeriodsDialog();
    const { workspaceId } = useScope();
    const [confirmedPeriodIds, setConfirmedPeriodIds] = useState<
        ReadonlySet<string>
    >(() => new Set());
    const [postingDates, setPostingDates] = useState<Record<string, string>>(
        {},
    );
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [isBusy, setIsBusy] = useState(false);

    const dueRows = useMemo(
        () => selectDueRows({ applications, confirmedPeriodIds }),
        [applications, confirmedPeriodIds],
    );
    const resolvePostingDate = useCallback(
        (row: MilestoneReviewRow) =>
            postingDates[row.periodId] ?? row.expectedDate,
        [postingDates],
    );
    const selectedPeriodIds = useMemo(
        () => getSelectedRowIds(rowSelection),
        [rowSelection],
    );

    const tableMeta = useMemo(
        () => ({
            callbacks: {
                onViewSchedule: (row) =>
                    navigate(
                        buildScheduleHubUrl({
                            scheduleApplicationId: row.applicationId,
                        }),
                    ),
                getPostingDate: resolvePostingDate,
                onPostingDateChange: (row, postingDate) =>
                    setPostingDates((current) => ({
                        ...current,
                        [row.periodId]: postingDate,
                    })),
            } satisfies MilestoneReviewTableCallbacks,
        }),
        [navigate, resolvePostingDate],
    );

    const confirmRows = useCallback(
        async (rows: MilestoneReviewRow[]) => {
            if (entityId == null || rows.length === 0) {
                return;
            }
            setIsBusy(true);
            const outcome = await confirmMilestoneRows({
                rows,
                recognize: (row) =>
                    recognizeScheduleMilestone({
                        workspaceId,
                        applicationId: row.applicationId,
                        sortOrder: row.sortOrder,
                        entityId,
                        postingDate: resolvePostingDate(row),
                    }),
            }).finally(() => {
                setIsBusy(false);
            });
            const { confirmedPeriodIds: confirmed, firstFailure } = outcome;
            if (outcome.lockedError !== null) {
                showLockedPeriods(outcome.lockedError);
            }
            const hiddenPeriodIds = [
                ...confirmed,
                ...outcome.alreadyPostedPeriodIds,
            ];
            if (hiddenPeriodIds.length > 0) {
                setConfirmedPeriodIds(
                    (current) => new Set([...current, ...hiddenPeriodIds]),
                );
                setRowSelection({});
            }
            const hasServerMovedOn =
                hiddenPeriodIds.length > 0 || outcome.failedCount > 0;
            if (hasServerMovedOn) {
                void invalidateAllTaskQueries(queryClient);
                invalidateAfterLifecycleAction(workspaceId);
            }
            const successToast = formatMilestoneConfirmToast({
                outcome,
                sourceLabel: resolveSharedSourceLabel(rows),
            });
            if (successToast !== null) {
                toast(successToast, SCHEDULE_REVIEW_TOAST_KEY);
            } else if (firstFailure) {
                error(firstFailure);
            }
        },
        [
            entityId,
            error,
            resolvePostingDate,
            showLockedPeriods,
            toast,
            workspaceId,
        ],
    );

    if (entityId == null) {
        return (
            <EmptyState
                header="This task is missing its entity"
                body="A new task appears after the next daily sync if milestones are still due."
            />
        );
    }
    if (applications.length === 0) {
        return (
            <EmptyState
                header="No milestones due"
                body="Every milestone that reached its expected date is confirmed. Kick marks this task done on the next daily sync."
            />
        );
    }

    return (
        <div className={styles.container}>
            {dueRows.length > 0 && (
                <TablePageSubHeader borderBottom>
                    {formatDueSummary(dueRows)}
                </TablePageSubHeader>
            )}
            <section className={styles.body}>
                <ViewTable<MilestoneReviewRow, MilestoneReviewColumn, never>
                    data={dueRows}
                    view={milestoneView}
                    config={milestoneReviewViewConfig}
                    tableMeta={tableMeta}
                    getRowId={(row) => row.periodId}
                    enableRowSelection
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection}
                    emptyView={() => (
                        <EmptyState
                            header="All due milestones confirmed"
                            body="Kick marks this task done on the next daily sync."
                        />
                    )}
                    footer={
                        <MilestoneReviewTaskActionBar
                            selectedCount={selectedPeriodIds.length}
                            totalCount={dueRows.length}
                            isBusy={isBusy}
                            onConfirmSelected={() =>
                                void confirmRows(
                                    dueRows.filter((row) =>
                                        selectedPeriodIds.includes(
                                            row.periodId,
                                        ),
                                    ),
                                )
                            }
                            onConfirmAll={() => void confirmRows(dueRows)}
                        />
                    }
                />
            </section>
        </div>
    );
};
